import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  MessageSquare,
  Phone,
  Send,
  Check,
  X,
  Clock,
  AlertCircle,
  Eye,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status badge mapping
const STATUS_CONFIG = {
  pending: { label: 'Ausstehend', color: 'bg-gray-100 text-gray-800', icon: Clock },
  queued: { label: 'In Warteschlange', color: 'bg-gray-100 text-gray-800', icon: Clock },
  sending: { label: 'Wird gesendet', color: 'bg-blue-100 text-blue-800', icon: Send },
  sent: { label: 'Gesendet', color: 'bg-blue-100 text-blue-800', icon: Send },
  delivered: { label: 'Zugestellt', color: 'bg-green-100 text-green-800', icon: Check },
  read: { label: 'Gelesen', color: 'bg-yellow-100 text-yellow-800', icon: Eye },
  failed: { label: 'Fehlgeschlagen', color: 'bg-red-100 text-red-800', icon: X },
  no_consent: { label: 'Kein Opt-in', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  invalid_number: { label: 'Ungültige Nummer', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  not_configured: { label: 'Nicht konfiguriert', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
  received: { label: 'Empfangen', color: 'bg-purple-100 text-purple-800', icon: MessageCircle },
  fallback_used: { label: 'Fallback verwendet', color: 'bg-orange-100 text-orange-800', icon: RefreshCw }
};

// Channel badge mapping
const CHANNEL_CONFIG = {
  sms: { label: 'SMS', color: 'bg-blue-500 text-white' },
  whatsapp: { label: 'WhatsApp', color: 'bg-green-500 text-white' }
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: Clock };
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

const ChannelBadge = ({ channel }) => {
  const config = CHANNEL_CONFIG[channel] || { label: channel, color: 'bg-gray-500 text-white' };
  
  return (
    <Badge className={config.color} data-testid={`channel-badge-${channel}`}>
      {config.label}
    </Badge>
  );
};

const MessageRow = ({ message, onClick }) => {
  const date = new Date(message.created_at);
  const formattedDate = date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  const isInbound = message.direction === 'inbound';
  
  return (
    <div 
      className={`p-3 border-b hover:bg-gray-50 cursor-pointer ${isInbound ? 'bg-purple-50' : ''}`}
      onClick={() => onClick(message)}
      data-testid={`message-row-${message.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {message.channel && <ChannelBadge channel={message.channel} />}
          <StatusBadge status={message.internal_status} />
          {isInbound && (
            <Badge className="bg-purple-100 text-purple-800">
              <MessageCircle className="h-3 w-3 mr-1" />
              Eingehend
            </Badge>
          )}
        </div>
        <span className="text-xs text-gray-500">{formattedDate}</span>
      </div>
      <p className="text-sm text-gray-700 line-clamp-2">
        {message.message_body || (message.template_key ? `Template: ${message.template_key}` : 'Keine Nachricht')}
      </p>
      {message.error_message && (
        <p className="text-xs text-red-500 mt-1">
          <AlertCircle className="h-3 w-3 inline mr-1" />
          {message.error_message}
        </p>
      )}
    </div>
  );
};

const MessageDetails = ({ message, onClose }) => {
  if (!message) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Nachrichtendetails</CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            {message.channel && <ChannelBadge channel={message.channel} />}
            <StatusBadge status={message.internal_status} />
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Richtung:</span>
              <span>{message.direction === 'inbound' ? 'Eingehend' : 'Ausgehend'}</span>
            </div>
            {message.to_number && (
              <div className="flex justify-between">
                <span className="text-gray-500">An:</span>
                <span>{message.to_number}</span>
              </div>
            )}
            {message.from_number && (
              <div className="flex justify-between">
                <span className="text-gray-500">Von:</span>
                <span>{message.from_number}</span>
              </div>
            )}
            {message.template_key && (
              <div className="flex justify-between">
                <span className="text-gray-500">Template:</span>
                <span>{message.template_key}</span>
              </div>
            )}
            {message.sent_at && (
              <div className="flex justify-between">
                <span className="text-gray-500">Gesendet:</span>
                <span>{new Date(message.sent_at).toLocaleString('de-DE')}</span>
              </div>
            )}
            {message.delivered_at && (
              <div className="flex justify-between">
                <span className="text-gray-500">Zugestellt:</span>
                <span>{new Date(message.delivered_at).toLocaleString('de-DE')}</span>
              </div>
            )}
            {message.read_at && (
              <div className="flex justify-between">
                <span className="text-gray-500">Gelesen:</span>
                <span>{new Date(message.read_at).toLocaleString('de-DE')}</span>
              </div>
            )}
            {message.provider_message_id && (
              <div className="flex justify-between">
                <span className="text-gray-500">Provider-ID:</span>
                <span className="text-xs font-mono">{message.provider_message_id}</span>
              </div>
            )}
          </div>
          
          {message.message_body && (
            <div>
              <span className="text-gray-500 text-sm">Nachricht:</span>
              <p className="mt-1 p-3 bg-gray-100 rounded-lg text-sm whitespace-pre-wrap">
                {message.message_body}
              </p>
            </div>
          )}
          
          {message.error_message && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <span className="text-red-700 text-sm font-medium">Fehler:</span>
              <p className="text-red-600 text-sm mt-1">{message.error_message}</p>
              {message.error_code && (
                <p className="text-red-500 text-xs mt-1">Code: {message.error_code}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default function CustomerMessaging({ customerId, customer, onCustomerUpdate }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Local state for settings form
  const [phoneNumber, setPhoneNumber] = useState(customer?.phone_number || customer?.phone || '');
  const [smsOptIn, setSmsOptIn] = useState(customer?.sms_opt_in || false);
  const [whatsappOptIn, setWhatsappOptIn] = useState(customer?.whatsapp_opt_in || false);
  const [preferredChannel, setPreferredChannel] = useState(customer?.preferred_messaging_channel || '');
  
  const token = localStorage.getItem('token');
  
  // Load messages
  const loadMessages = useCallback(async () => {
    if (!customerId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/messaging/customer/${customerId}/messages?limit=20`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  }, [customerId, token]);
  
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);
  
  // Update local state when customer prop changes
  useEffect(() => {
    if (customer) {
      setPhoneNumber(customer.phone_number || customer.phone || '');
      setSmsOptIn(customer.sms_opt_in || false);
      setWhatsappOptIn(customer.whatsapp_opt_in || false);
      setPreferredChannel(customer.preferred_messaging_channel || '');
    }
  }, [customer]);
  
  // Save messaging settings
  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/messaging/customer/${customerId}/messaging-settings`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            phone_number: phoneNumber || null,
            sms_opt_in: smsOptIn,
            whatsapp_opt_in: whatsappOptIn,
            preferred_messaging_channel: preferredChannel || null
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Messaging-Einstellungen gespeichert');
        if (onCustomerUpdate && data.customer) {
          onCustomerUpdate(data.customer);
        }
        setShowSettings(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSettingsLoading(false);
    }
  };
  
  const displayedMessages = showAllMessages ? messages : messages.slice(0, 3);
  
  return (
    <Card data-testid="customer-messaging-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          SMS & WhatsApp
        </CardTitle>
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            data-testid="toggle-messaging-settings"
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={loadMessages}
            data-testid="refresh-messages"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Consent Status Overview */}
        <div className="flex flex-wrap gap-2" data-testid="consent-badges">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              {customer?.phone_number_normalized || customer?.phone || 'Keine Nummer'}
            </span>
          </div>
          <Badge className={smsOptIn ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
            SMS: {smsOptIn ? 'Ja' : 'Nein'}
          </Badge>
          <Badge className={whatsappOptIn ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
            WhatsApp: {whatsappOptIn ? 'Ja' : 'Nein'}
          </Badge>
          {preferredChannel && preferredChannel !== 'none' && (
            <Badge className="bg-blue-100 text-blue-800">
              Bevorzugt: {preferredChannel === 'whatsapp' ? 'WhatsApp' : preferredChannel === 'sms' ? 'SMS' : preferredChannel}
            </Badge>
          )}
        </div>
        
        {/* Settings Form */}
        {showSettings && (
          <div className="p-4 bg-gray-50 rounded-lg space-y-4" data-testid="messaging-settings-form">
            <h4 className="font-medium text-sm">Messaging-Einstellungen</h4>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+49 170 1234567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                data-testid="phone-number-input"
              />
              <p className="text-xs text-gray-500">
                Wird automatisch ins E.164-Format normalisiert
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="sms-opt-in">SMS Einwilligung</Label>
              <Switch
                id="sms-opt-in"
                checked={smsOptIn}
                onCheckedChange={setSmsOptIn}
                data-testid="sms-opt-in-switch"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="whatsapp-opt-in">WhatsApp Einwilligung</Label>
              <Switch
                id="whatsapp-opt-in"
                checked={whatsappOptIn}
                onCheckedChange={setWhatsappOptIn}
                data-testid="whatsapp-opt-in-switch"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="preferred-channel">Bevorzugter Kanal</Label>
              <Select value={preferredChannel || "no_preference"} onValueChange={(val) => setPreferredChannel(val === "no_preference" ? "" : val)}>
                <SelectTrigger data-testid="preferred-channel-select">
                  <SelectValue placeholder="Keine Präferenz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_preference">Keine Präferenz</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">E-Mail</SelectItem>
                  <SelectItem value="none">Keine Nachrichten</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowSettings(false)}
              >
                Abbrechen
              </Button>
              <Button 
                size="sm"
                onClick={saveSettings}
                disabled={settingsLoading}
                data-testid="save-messaging-settings"
              >
                {settingsLoading ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </div>
        )}
        
        {/* Messages List */}
        <div className="border rounded-lg" data-testid="messages-list">
          {loading ? (
            <div className="p-4 text-center text-gray-500">Lade Nachrichten...</div>
          ) : messages.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              Keine Nachrichten vorhanden
            </div>
          ) : (
            <>
              {displayedMessages.map((message) => (
                <MessageRow
                  key={message.id}
                  message={message}
                  onClick={setSelectedMessage}
                />
              ))}
              
              {messages.length > 3 && (
                <button
                  className="w-full p-2 text-center text-sm text-blue-600 hover:bg-gray-50 flex items-center justify-center gap-1"
                  onClick={() => setShowAllMessages(!showAllMessages)}
                  data-testid="toggle-show-all-messages"
                >
                  {showAllMessages ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Weniger anzeigen
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Alle {messages.length} Nachrichten anzeigen
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
        
        {/* Warning if no consent */}
        {!smsOptIn && !whatsappOptIn && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <AlertCircle className="h-4 w-4 inline mr-2" />
            Kein SMS- oder WhatsApp-Opt-in vorhanden. Bitte Einwilligung des Kunden einholen.
          </div>
        )}
      </CardContent>
      
      {/* Message Details Modal */}
      {selectedMessage && (
        <MessageDetails 
          message={selectedMessage} 
          onClose={() => setSelectedMessage(null)} 
        />
      )}
    </Card>
  );
}
